// Pantalla de perfil de amigo (versión web).
// Muestra información del perfil, permite interacciones (visita, like) y acciones (bloquear, reportar).
// Recibe el friendId via location.state para no exponer UUIDs en la URL.
// Adaptado para web desde amarsi-app (React Native a React DOM).

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthQuery } from '../../hooks/useAuthQuery';
import { useFriendProfileQuery, useFriendInteractionsQuery } from '../../hooks/useFriendQueries';
import { useBlockUserMutation, useLikeUserMutation } from '../../hooks/useFriendMutations';
import { useToast } from '../../hooks/useToast';
import CarouselCards from '../../components/CarouselCards';
import InputChatBox from '../../components/chat/InputChatBox';
import InputChatV2 from '../../components/chat/InputChatV2';
import { FiArrowLeft, FiMoreVertical, FiHeart, FiInstagram, FiTwitter } from 'react-icons/fi';
import { defaultImage } from '../../constants/defaultImage';
import { femRol, homRol, femEtnia, homEtnia, femFisic, homFisic } from '../../constants/humanQualities';

export default function FriendProfileScreen() {
  const location = useLocation();
  const navigate = useNavigate();

  // Obtiene friendId de location.state para no exponer UUID en la URL
  const friendId = location.state?.friendId;

  // Si no hay friendId, redirige al radar
  useEffect(() => {
    if (!friendId) {
      console.error('[FriendProfileScreen] No se recibió friendId en location.state');
      navigate('/radar');
    }
  }, [friendId, navigate]);

  // Obtiene la sesión del usuario
  const { sessionQuery } = useAuthQuery();
  const session = sessionQuery.data?.session;
  const user = session?.user;

  // Hook para notificaciones Toast
  const { showError, showSuccess, showInfo } = useToast({ position: 'bottom-center' });

  const [modalVisible, setModalVisible] = useState(false);
  const [messageText, setMessageText] = useState('');
  
  // Ref para hacer tracking de si ya se registró la visita
  const visitRegisteredRef = useRef(false);

  // Hooks de TanStack Query para datos del amigo e interacciones
  const { data: friendData, isLoading: loadingUser, refetch: refetchProfile } = useFriendProfileQuery(friendId); // eslint-disable-line @typescript-eslint/no-unused-vars
  const { data: interactionsData, refetch: refetchInteractions } = useFriendInteractionsQuery(user?.id, friendId); // eslint-disable-line @typescript-eslint/no-unused-vars

  // Mutaciones para acciones
  const blockMutation = useBlockUserMutation();
  const likeMutation = useLikeUserMutation();

  // Estados derivados de las queries
  const friend = useMemo(() => friendData?.[0] || {}, [friendData]);
  const interactions = useMemo(() => interactionsData?.[0] || {}, [interactionsData]);
  const meDioLike = interactions.te_dio_like;
  const diLike = interactions.yo_di_like;

  // Calcula tiempo desde última conexión
  const userTime = useMemo(() => {
    if (!friend.ultimaconexion) return 1000;
    const date = new Date(friend.ultimaconexion);
    const now = new Date();
    return Math.floor((now - date) / (1000 * 60)); // minutos
  }, [friend.ultimaconexion]);

  // Callbacks para el modal
  const handlePresentModalPress = useCallback(() => {
    setModalVisible(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
  }, []);

  // Función para crear impeachment (reporte)
  // Navega a la pantalla de reporte pasando los datos del usuario por location.state
  const createImpeachment = useCallback(() => {
    navigate('/report', { state: friend });
  }, [friend, navigate]);

  // Maneja el bloqueo del usuario
  const handleBlockPerson = useCallback(async () => {
    try {
      await blockMutation.mutateAsync({ gestorId: user?.id, bloqueadoId: friendId });
      navigate('/radar');
    } catch (error) {
      console.error('Error bloqueando usuario:', error);
      showError(error.message || 'Error al bloquear usuario');
    }
  }, [blockMutation, user?.id, friendId, navigate, showError]);

  // Confirmación para bloquear usuario
  const confirmBlockPerson = useCallback(() => {
    if (window.confirm(`¿Bloquear a ${friend.full_name}? Ya no volveran a verse, hablar ni enviarse likes.`)) {
      handleBlockPerson();
    }
  }, [friend.full_name, handleBlockPerson]);

  // Completa el envío de mensaje
  // Navega a chatroom con datos del destinatario
  const onCompleteSend = useCallback((chatId) => {
    console.log('onCompleteSend: Navegando al chatroom:', chatId, 'con amigo:', friendId);
    navigate('/chats/chatroom', {
      state: {
        chatroom_id: chatId,
        friend_id: friendId,
        destinatario: { full_name: friend.full_name, tca: friend.tca, friend_id: friendId },
        avatar: friend.user_image_avatars?.length ? friend.user_image_avatars[0].url : null,
        thread: friend.full_name
      }
    });
  }, [friendId, friend.full_name, friend.tca, friend.user_image_avatars, navigate]);

  // Gestiona nuevas interacciones (visita o like)
  const newInteraction = useCallback(async (descripcion, tipo) => {
    console.log('newInteraction: tipo:', tipo, 'descripcion:', descripcion);
    
    if (!user?.id) {
      showError('Sesión no válida');
      return;
    }

    const parametros = {
      friend_id: friendId,
      tipo: tipo
    };

    try {
      await likeMutation.mutateAsync(parametros);

      if (tipo === 1) {
        console.log('Visita registrada');
      } else if (tipo === 2) {
        showSuccess(`Enviaste un Like a ${friend.full_name}. Podrás enviar otro en 24hs.`);
      }
    } catch (error) {
      console.error('Error en interacción:', error);
      showError(error.message || 'Error en interacción');
    }
  }, [friendId, friend.full_name, likeMutation, user?.id, showError, showSuccess]);

  // Abre enlaces a redes sociales
  const linkTo = (link, username) => {
    const linkURL = link + username;
    window.open(linkURL, '_blank');
  };

  // Efecto para registrar visita al cargar (solo una vez)
  useEffect(() => {
    if (friend.full_name && user?.id && !visitRegisteredRef.current) {
      console.log('Registrando visita al cargar perfil de:', friend.full_name);
      visitRegisteredRef.current = true;
      
      // Registrar visita directamente sin depender de newInteraction
      const registerVisit = async () => {
        if (!user?.id) return;
        
        const parametros = {
          friend_id: friendId,
          tipo: 1
        };

        try {
          await likeMutation.mutateAsync(parametros);
          console.log('Visita registrada');
        } catch (error) {
          console.error('Error registrando visita:', error);
        }
      };

      setTimeout(() => {
        registerVisit();
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friend.full_name, user?.id]);

  // Componente para botón de like
  const ActionMessageComponent = () => (
    <button
      onClick={() => {
        !diLike ? newInteraction('', 2) : showInfo('Ya le enviaste un Like a esta persona. Podrás enviar otro en 24hs.');
      }}
      className={`w-11 h-11 flex items-center justify-center rounded-full border ${
        diLike ? 'bg-primary' : 'bg-neutral-800'
      } border-neutral-700`}
    >
      <FiHeart size={24} color={diLike ? 'white' : 'rgba(255,255,255,0.3)'} />
    </button>
  );

  if (loadingUser) {
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center">
        <p className="text-white">Cargando perfil...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-900 flex flex-col">
      {/* Carousel de fotos */}
      <div className="relative">
        {friend.user_image_avatars?.length > 0 ? (
          <CarouselCards data={friend.user_image_avatars} />
        ) : (
          <div className="w-full aspect-square bg-neutral-800 flex items-center justify-center">
            <img
              src={defaultImage}
              alt="Avatar por defecto"
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Botón de volver */}
        <button
          onClick={() => navigate(-1)}
          className="absolute left-4 top-4 bg-neutral-900/75 hover:bg-neutral-900 rounded-full w-12 h-12 flex items-center justify-center transition-colors"
        >
          <FiArrowLeft size={24} color="white" />
        </button>

        {/* Botón de opciones */}
        <button
          onClick={handlePresentModalPress}
          className="absolute right-4 top-4 bg-neutral-900/75 hover:bg-neutral-900 rounded-full w-12 h-12 flex items-center justify-center transition-colors"
        >
          <FiMoreVertical size={24} color="white" />
        </button>

        {/* Indicador de "Te dio un Like" */}
        {meDioLike && (
          <div className="absolute -mt-8 left-0 flex items-center rounded-tr-xl bg-red-600/80 px-5 h-8">
            <FiHeart size={18} color="white" />
            <span className="text-base ml-2 mr-2 text-white">
              Te dio un Like
            </span>
          </div>
        )}
      </div>

      {/* Información básica */}
      <div className="bg-neutral-800 border-b border-neutral-700 p-4">
        <div className="flex flex-row justify-between items-center">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${userTime < 6 ? 'bg-green-500' : 'bg-neutral-500'}`} />
            <h2 className="text-lg text-white truncate max-w-[calc(100vw-150px)]">
              {friend.full_name}
            </h2>
          </div>
          {friend.years && (
            <span className="text-lg text-white">
              {friend.years} Años
            </span>
          )}
        </div>
      </div>

      {/* Contenido scrollable */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Bio */}
        <div className="mb-8">
          <h3 className="text-base text-neutral-400 pb-4">Acerca de mí:</h3>
          <div className="bg-neutral-800 rounded-xl rounded-tl-none">
            {friend.bio ? (
              <p className="text-base text-neutral-300 p-4">
                "{friend.bio}"
              </p>
            ) : (
              <p className="text-base text-neutral-300 text-center p-4">
                {friend.full_name} no escribió nada aquí.
              </p>
            )}
          </div>
        </div>

        {/* Información personal */}
        {friend.etnia > 0 && (
          <div className="p-2 border-b border-neutral-700 flex justify-between items-center">
            <span className="text-base text-neutral-400">Etnia</span>
            <span className="text-base text-neutral-400">
              {friend.sex === 1 ? femEtnia[friend.etnia] || 'Sin respuesta' : homEtnia[friend.etnia] || 'Sin respuesta'}
            </span>
          </div>
        )}

        {friend.rol > 0 && (
          <div className="p-2 border-b border-neutral-700 flex justify-between items-center">
            <span className="text-base text-neutral-400">Rol</span>
            <span className="text-base text-neutral-400">
              {friend.sex === 1 ? femRol[friend.rol] || 'Sin respuesta' : homRol[friend.rol] || 'Sin respuesta'}
            </span>
          </div>
        )}

        {friend.peso > 0 && (
          <div className="p-2 border-b border-neutral-700 flex justify-between items-center">
            <span className="text-base text-neutral-400">Peso</span>
            <span className="text-base text-neutral-400">{friend.peso} Kg.</span>
          </div>
        )}

        {friend.complexion > 0 && (
          <div className="p-2 border-b border-neutral-700 flex justify-between items-center">
            <span className="text-base text-neutral-400">Complexión</span>
            <span className="text-base text-neutral-400">
              {friend.sex === 1 ? femFisic[friend.complexion] || 'Sin respuesta' : homFisic[friend.complexion] || 'Sin respuesta'}
            </span>
          </div>
        )}

        {friend.enbuscade > 0 && (
          <div className="p-2 border-b border-neutral-700 flex justify-between items-center">
            <span className="text-base text-neutral-400">En busca de</span>
            <span className="text-base text-neutral-400">
              {friend.enbuscade === 1 ? 'Amistad' : 'Amor'} {/* Placeholder - usar constantes */}
            </span>
          </div>
        )}

        {/* Redes sociales */}
        <div className="flex flex-row p-4 gap-4">
          {friend.instagram && (
            <button
              onClick={() => linkTo('https://www.instagram.com/', friend.instagram)}
              className="flex flex-row gap-4 bg-neutral-800 items-center rounded-full p-3 px-6 hover:bg-neutral-700 transition-colors"
            >
              <FiInstagram size={20} color="white" />
              <span className="text-base text-neutral-300">{friend.instagram}</span>
            </button>
          )}

          {friend.twitter && (
            <button
              onClick={() => linkTo('https://www.twitter.com/', friend.twitter)}
              className="flex flex-row gap-4 bg-neutral-800 items-center rounded-full p-3 px-6 hover:bg-neutral-700 transition-colors"
            >
              <FiTwitter size={20} color="white" />
              <span className="text-base text-neutral-300">{friend.twitter}</span>
            </button>
          )}
        </div>

        <div className="h-16" />
      </div>

      {/* Input de mensaje - V1 */}
      <div className="bg-neutral-900 sticky bottom-[4.5rem]">
        <InputChatBox
          destinatario={{ full_name: friend.full_name, tca: friend.tca, friend_id: friendId }}
          onComplete={onCompleteSend}
          actionComponent={<ActionMessageComponent />}
          maxLength={300}
          messageText={messageText}
          setMessageText={setMessageText}
        />
      </div>

      {/* Input de mensaje V2 con MUI Chat - comentado */}
      {/* <div className="bg-neutral-900 flex">
        <InputChatV2
          friendId={friendId}
          friendName={friend.full_name}
          onComplete={onCompleteSend}
        />
      </div> */}

      {/* Modal de opciones */}
      {modalVisible && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-neutral-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-white text-lg mb-4 text-center">¿Pasa algo con este perfil?</h3>
            <div className="space-y-4">
              <button
                onClick={createImpeachment}
                className="w-full bg-neutral-700 text-white p-4 rounded-lg hover:bg-neutral-600 transition-colors flex items-center justify-between"
              >
                <span>Reportar</span>
                <span>🚩</span>
              </button>
              <button
                onClick={confirmBlockPerson}
                className="w-full bg-neutral-700 text-white p-4 rounded-lg hover:bg-neutral-600 transition-colors flex items-center justify-between"
              >
                <span>Bloquear</span>
                <span>🚫</span>
              </button>
            </div>
            <button
              className="w-full mt-4 bg-neutral-600 text-white p-4 rounded-lg hover:bg-neutral-500 transition-colors"
              onClick={handleCloseModal}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
